import cn from 'classnames';

import { memo, useMemo } from 'react';
import { ClanRole, Menu, DivTooltip, Tooltip, TooltipBody } from '@wg/wows-react-uikit';

import { t } from '~/helpers/localization';
import { getLocalizedName } from '~/roles';

import styles from './RoleSelect.scss';

import type { ROLE_NAMES } from '~/roles';
import type { Nullable } from '~/types/generics';

interface RoleSelectProps {
  roles: ROLE_NAMES[];
  selected: Nullable<ROLE_NAMES>;
  current: Nullable<ROLE_NAMES>;
  onChange: (value: ROLE_NAMES) => void;
}

const tooltipBody = (
  <Tooltip>
    <TooltipBody>{t('This position is already applied')}</TooltipBody>
  </Tooltip>
);

const RoleSelect = ({ roles, selected, current, onChange }: RoleSelectProps) => {
  const handleChange = (value: Nullable<ROLE_NAMES>) => {
    if (!value) {
      return;
    }
    onChange(value);
  };

  const items = useMemo(() => {
    return roles.map((roleName) => {
      const isCurrent = roleName === current;

      return {
        value: roleName,
        content: (
          <DivTooltip tooltipBody={isCurrent ? tooltipBody : undefined}>
            <div
              onClick={isCurrent ? (e) => e.stopPropagation() : undefined}
              className={cn(styles.item, {
                [styles.disabled]: isCurrent,
              })}
            >
              <ClanRole role={roleName} />
              &nbsp;{getLocalizedName(roleName)}
            </div>
          </DivTooltip>
        ),
      };
    });
  }, [roles, current]);

  return (
    <div className={styles.container}>
      <Menu items={items} itemSelected={selected} onItemChange={handleChange}>
        <div className={styles.label}>
          {selected ? (
            <>
              <ClanRole role={selected} />
              &nbsp;{getLocalizedName(selected)}
            </>
          ) : (
            t('Select Role')
          )}
        </div>
      </Menu>
    </div>
  );
};

export default memo(RoleSelect);
